import * as React from 'react';
import { Button, Icon, Input } from 'kui';
import { IColorTagsDropdownProps } from './types';
import {
    CARD_COLOR_TAGS_ALL,
    CARD_COLOR_TAGS_BOARD
} from '../../../../../aside_panel/cardDetails/ColorTags/components/CardColorTags/constants';
import { ColorTagsSelect } from '../ColorTagsSelect/ColorTagsSelect';
import { CARD_COLOR_TAGS_LIMIT } from '../../../../../../../const';
import {
    COLOR_TAGS_BUTTON_EDIT,
    COLOR_TAGS_BUTTON_EDIT_SHORT,
    COLOR_TAGS_BUTTON_NEW,
    COLOR_TAGS_BUTTON_NEW_SHORT,
    COLOR_TAGS_INPUT_PLACEHOLDER,
    COLOR_TAGS_INPUT_PLACEHOLDER_EDIT,
    EDIT_COLOR_TAGS
} from './constants';
import './_ColorTagsDropdown.scss';
import {
    SegmentCardColorTagAddSourceValue,
    SegmentCardColorTagClickAddSourceValue
} from '../../../../../../../middlewares/segment/trackEntities/cardEvents';
import { IColor, TColorTagColor } from '../../../../../../../types/model';
import { ColorPalette } from '../../../ColorPalette/ColorPalette/ColorPalette';
import { IColorTag, TColorId } from '../../../../../../../types/types';
import {
    CardColorTagsTooltipHOC
} from '../../../../../aside_panel/cardDetails/ColorTags/hocs/CardColorTagsTooltipHOC/CardColorTagsTooltipHOC';
import { DEFAULT_COLORS } from '../../hocs/ColorTagsDropdownHOC/constants';
import { CLASS_COLOR_TAGS_DROPDOWN, CLASS_COLOR_TAGS_PALETTE } from '../../constants';
import { CLASS_CARD_DETAILS } from '../../../../../aside_panel/cardDetails';
import { isEqual } from 'underscore';

export function ColorTagsDropdown ({
    asidePanelWidth,
    boardCardColors,
    editingColor,
    boardColors,
    colorTags,
    customColors,
    isAutoFocus = true,
    isReset,
    isSmall,
    paletteVariant,
    userRecentColorTags,
    onAddCustomColor,
    onChange,
    onClose,
    onEdit,
    onCreate,
    onDidMount,
    onDoubleClick,
    onDropdownOpen,
    onOpenSettings,
    onSave,
    onSetEditing,
    onSectionAllClick,
    onSectionUsedClick,
    onShowSnackbar,
    setInputFocus,
    setRemovingSource,
    setRemovingId,
}: IColorTagsDropdownProps) {
    const className = CLASS_COLOR_TAGS_DROPDOWN;
    const classInput = className + '__input';

    const isLimit = colorTags.length >= CARD_COLOR_TAGS_LIMIT;

    const getRecentAndCardColors = (cardColorTags: IColorTag[] = colorTags) => {
        return [...new Set([...userRecentColorTags, ...cardColorTags.map(colorTag => colorTag.id)])];
    };

    let [query, setQuery] = React.useState('');
    const [colorLabel, setColorLabel] = React.useState(editingColor);
    let [color, _setColor] = React.useState(null);
    let [availableBoardCardColors, _setAvailableBoardCardColors] = React.useState(boardCardColors);
    let [availableBoardColors, _setAvailableBoardColors] = React.useState(boardColors.filter(onColorsFilter));
    const [isBoardColorsOpened, setBoardColorsOpened] = React.useState(!!availableBoardCardColors.length);
    const [isAllColorsOpened, setIsAllColorsOpened] = React.useState(!availableBoardCardColors.length);
    const [isAllColorsForceOpened, setAllColorsForceOpened] = React.useState(null);
    const [isError, setError] = React.useState(null);
    const [isNew, setNew] = React.useState(null);
    const [isNewEditing, setNewEditing] = React.useState(null);
    const [recentAndCardColors, setRecentAndCardColors] = React.useState(getRecentAndCardColors());
    let [isFocus, _setFocus] = React.useState(null);

    const setFocus = (value: boolean) => {
        isFocus = value;
        _setFocus(value);
    };

    const setAvailableBoardCardColors = (colors: IColor[]) => {
        availableBoardCardColors = colors;
        _setAvailableBoardCardColors(colors);
    };

    const onColorEditSelect = (color: IColor) => {
        onDoubleClick();
        setColorLabel(color);
    };

    const setAvailableBoardColors = (colors: IColor[]) => {
        availableBoardColors = colors;
        _setAvailableBoardColors(colors);
    };

    const setColor = (newColor: TColorTagColor) => {
        color = newColor
        _setColor(newColor);
    };

    const isEdit = colorLabel || isNewEditing || (availableBoardCardColors && !availableBoardCardColors.length && !availableBoardColors.length);

    const inputRef = React.useRef(null);

    function onColorsFilter(color: IColor) {
        return !availableBoardCardColors.find(boardColor => boardColor.id === color.id);
    }

    const onInputFocus = (force?: boolean) => {
        if (inputRef.current && (isAutoFocus || force)) {
            inputRef.current.setFocus();
        }
    };

    const onColorSelect = (
        colorTagId: TColorId,
        clickSource: SegmentCardColorTagClickAddSourceValue
    ) => {
        const isUpdateRecent = isFocus;
        setFocus(false);
        const isInitial = isShowRecent && showRecentAndCardColors.find(colorTag => colorTagId === colorTag.id);
        if (!isSmall && isAutoFocus) {
            onInputFocus();
        } else {
            const container = document.querySelector(`.${className}`) as HTMLElement;
            if (container) {
                container.focus();
            }
        }
        onChange(colorTagId, clickSource,isInitial ? SegmentCardColorTagAddSourceValue.INITIAL : SegmentCardColorTagAddSourceValue.AFTER_SEARCH).then(result => {
            if (!result) {
                setRemovingId(colorTagId);
                if (setRemovingSource) setRemovingSource(clickSource);
                return;
            }
            if (isUpdateRecent) {
                let cardColorTags = [...colorTags];
                const index = cardColorTags.findIndex(colorTag => colorTagId === colorTag.id);
                if (index === -1) {
                    cardColorTags = [...cardColorTags, boardColors.find(colorTag => colorTagId === colorTag.id)];
                } else {
                    cardColorTags = cardColorTags.filter(colorTag => colorTagId === colorTag.id);
                }
                setRecentAndCardColors(getRecentAndCardColors(cardColorTags));
            }
            if (isSmall || isAutoFocus) {
                setTimeout(() => {
                    const color = document.querySelector('.' + CLASS_CARD_DETAILS + '__color-tags-item--' + colorTagId) as HTMLElement;
                    if (color) color.focus();
                }, 0);
            }
        });
    };

    const onQueryChange = (e: React.SyntheticEvent) => {
        const { value } = e.target as HTMLInputElement;
        setQuery(value);
        setError(false);
    };

    const onColorChange = (newColor: TColorTagColor, isNew: boolean) => {
        if (isNew) {
            setError(false);
            onAddCustomColor(newColor).then(() => {
                setColor(newColor);
            });
        } else if (newColor !== color) {
            setColor(newColor);
            setError(false);
            if (!colorLabel) { // чтобы не возвращаться к поиску при измнении инпута
                setNewEditing(true);
            }
        }
    };

    const onBack = () => {
        if (editingColor) {
            onClose();
        } else {
            setQuery('');
            setColor(null);
            setColorLabel(null);
            setNewEditing(false);
            if (colorLabel && !availableBoardCardColors.find(label => label.id === colorLabel.id)) {
                setAllColorsForceOpened(true);
            }
            if (onSetEditing) onSetEditing(false);
        }
    };

    const handleSave = () => {
        if (!query || !color) return;
        if (colorLabel) {
            const isExist = !!boardColors.find(colorTag => {
                return colorTag.id !== colorLabel.id &&
                    colorTag.color === color &&
                    colorTag.name.toLowerCase() === query.toLowerCase();
            });
            if (isExist) {
                setError(true);
                onShowSnackbar();
                if (!isSmall) setTimeout(onInputFocus, 0);
                return;
            }
            onEdit({ id: colorLabel.id, color, name: query });
            setNeedUpdate(true);
        } else if (!isLimit) {
            onSave({ color, name: query }).then((colorTag) => {
                const isInitial = isShowRecent && showRecentAndCardColors.find(color => colorTag.id === color.id);
                onChange(colorTag.id, SegmentCardColorTagClickAddSourceValue.NOT_CHECKBOX,isInitial ? SegmentCardColorTagAddSourceValue.INITIAL : SegmentCardColorTagAddSourceValue.AFTER_SEARCH);
            });
        }
        onBack();
    };

    const setNewColor = () => {
        const colorsLength = DEFAULT_COLORS.length
        let newColor = DEFAULT_COLORS[Math.floor(Math.random() * colorsLength)]; // random color if none found
        for (let i = 0; i < colorsLength; i++) {
            const color = DEFAULT_COLORS[i]
            if (!boardCardColors.find(tag => tag.color === color)) {
                newColor = color;
                break;
            }
        }
        setColor(newColor);
    };

    const onNewClick = () => {
        if (
            isNew &&
            !isNewEditing &&
            (availableBoardColors.length || availableBoardCardColors.length)
        ) {
            setNewEditing(true);
            setNewColor();
            if (!isSmall) onInputFocus();
        } else {
            handleSave();
        }
        if (!colorLabel && !isLimit) {
            onCreate();
        }
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (isEdit) {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onBack();
                return;
            }
            if (e.key === 'Enter') {
                e.stopPropagation();
                if (!colorLabel && !isLimit) {
                    onCreate();
                }
                onNewClick();
            }
        } else if (isSmall && isFocus) {
            if (e.key === 'Escape') {
                e.stopPropagation();
                setFocus(false);
                const el = document.querySelector(`.${className}`) as HTMLElement;
                if (el) el.focus();
                return;
            }
        } else {
            if (e.key === 'Enter') {
                e.stopPropagation();
                if (isNew && !isNewEditing) {
                    setNewEditing(true);
                    setNewColor();
                } else {
                    const colors = [...availableBoardColors, ...availableBoardCardColors];
                    if (query && colors.length === 1) { // если 100% совподение
                        if (availableBoardCardColors.length) { // лэйбел включен - сбрасываем поиск
                            onBack();
                        } else { // лэйбел выключен - добавляем на карту
                            const isInitial = isShowRecent && showRecentAndCardColors.find(colorTag => colorTag.id === colors[0].id);
                            onChange(colors[0].id, SegmentCardColorTagClickAddSourceValue.NOT_CHECKBOX, isInitial ? SegmentCardColorTagAddSourceValue.INITIAL : SegmentCardColorTagAddSourceValue.AFTER_SEARCH);
                            if (!isSmall) onInputFocus();
                        }
                    }
                }
            }
        }
    };

    React.useEffect(() => {
        if (colorLabel) {
            setColor(colorLabel.color);
            setQuery(colorLabel.name);
            if (onSetEditing) onSetEditing(true);
            setTimeout(() => { // wait for dropdown to open
                const input = inputRef.current && inputRef.current.getInput();
                if (input) {
                    input.setSelectionRange(0, colorLabel.name.length);
                }
            }, 50);
        }
    }, [colorLabel]);

    React.useEffect(() => {
        if (!isSmall) return;
        onSetEditing(isFocus && !!onSetEditing);
    }, [isSmall, isFocus]);

    React.useEffect(() => {
        if (isSmall && isEdit) {
            onInputFocus(true);
        }
    }, [isEdit]);

    React.useEffect(() => {
        if (colorLabel) return;
        let isNew = false; // когда инпут пуст скрываем создание нового
        if (query) {
            isNew = true; // преполагаем, что нужно показывать создание нового
            const search = query.toLowerCase();
            setAvailableBoardCardColors(boardCardColors.filter(colorTag => {
                const colorName = colorTag.name.toLowerCase();
                if (search === colorName) {
                    isNew = false; // имя тэга совпадает - скрываем
                }
                return colorName.includes(search);
            }));
            setAvailableBoardColors(
                boardColors
                    .filter(colorTag => {
                        const colorName = colorTag.name.toLowerCase();
                        if (search === colorName) {
                            isNew = false; // имя тэга совпадает - скрываем
                        }
                        return !availableBoardCardColors.find(boardColor => boardColor.id === colorTag.id) &&
                            colorName.includes(search);
                    })
            );
            if (// одно из имен совпало с инпутом, но скрываем только когда 100% совподение
                !isNew &&
                availableBoardColors.length + availableBoardCardColors.length !== 1
            ) {
                isNew = true;
            }
        } else {
            setAvailableBoardCardColors(boardCardColors);
            setAvailableBoardColors(boardColors.filter(colorTag => {
                return !availableBoardCardColors.find(boardColor => boardColor.id === colorTag.id);
            }));
        }
        setNew(isNew);
        if (!color && !availableBoardCardColors.length && !availableBoardColors.length) {
            setNewColor();
        }
        if (!isSmall) setTimeout(onInputFocus, 0); // set focus from color to input
    }, [query, boardColors, boardCardColors]);

    React.useEffect(() => {
        if (!isSmall) return;
        setTimeout(() => { // ждем открытия, чтобы дать возможность выбирать с помощью tab
            const el = document.querySelector(`.${className} .${CLASS_CARD_DETAILS}__color-tags-item`) as HTMLElement;
            if (el) el.focus();
        }, 150);
    }, []);

    React.useEffect(() => {
        onDidMount();
        if (onDropdownOpen) onDropdownOpen(boardCardColors.length, boardColors.length);
    }, []);

    React.useEffect(() => {
        if (isReset && (isEdit || isSmall)) {
            setAvailableBoardCardColors(boardCardColors);
            setAvailableBoardColors(boardColors.filter(onColorsFilter));
            onBack();
        }
    }, [isReset]);

    React.useEffect(() => {
        if (isSmall) return;
        setTimeout(onInputFocus, 0); // set focus from color to input
    }, [colorLabel, color]);

    React.useEffect(() => {
        setColorLabel(editingColor);
    }, [editingColor]);

    React.useEffect(() => {
        setIsAllColorsOpened(!availableBoardCardColors.length);
    }, []);

    React.useEffect(() => {
        const dropdown = document.querySelector('.card-details__color-tags-dropdown') as HTMLElement;
        if (!dropdown) return;
        if (isEdit) {
            dropdown.classList.add(CLASS_COLOR_TAGS_PALETTE)
        } else {
            dropdown.classList.remove(CLASS_COLOR_TAGS_PALETTE)
        }
    }, [isEdit]);

    const [isNeedUpdate, setNeedUpdate] = React.useState(null);
    React.useEffect(() => {
        if (isNeedUpdate) {
            setAvailableBoardCardColors(boardCardColors);
            setAvailableBoardColors(boardColors.filter(onColorsFilter));
            setNeedUpdate(false);
        }
    }, [isNeedUpdate]);

    const isShowSave = !isEdit && isNew ||
        isEdit && (
            (
                !availableBoardCardColors.length &&
                !availableBoardColors.length ||
                isNewEditing
            ) ||
            colorLabel
        );

    const isButtonDisabled = (
            isNewEditing ||
            isEdit && !availableBoardCardColors.length && !availableBoardColors.length
        ) && (!query || isError) ||
        colorLabel && colorLabel.color === color && colorLabel.name === query;

    const isShowRecent = isSmall && !!recentAndCardColors.length && !isFocus && !query;
    const showRecentAndCardColors = [...new Set([...availableBoardCardColors, ...availableBoardColors])].filter(colorTag => recentAndCardColors.includes(colorTag.id))
    return (
        <div className={className} onKeyDown={onKeyDown} tabIndex={0}>
            <div className={classInput + '-container'}>
                <Input
                    ref={inputRef}
                    className={`
                        ${classInput}
                        ${isShowSave && isSmall ? classInput + '--short' : ''}
                        ${isShowSave && !colorLabel ? classInput + '--new' : ''}
                        ${isShowSave && colorLabel ? classInput + '--edit' : ''}
                    `}
                    autoFocus={isAutoFocus}
                    placeholder={isEdit ? COLOR_TAGS_INPUT_PLACEHOLDER_EDIT : COLOR_TAGS_INPUT_PLACEHOLDER}
                    value={query}
                    onChange={onQueryChange}
                    autosize={false}
                    state={isEdit && isError ? 'error' : null}
                    onFocus={() => {
                        if (setInputFocus) setInputFocus(true);
                        if (!isSmall) return;
                        setFocus(true);
                    }}
                    onBlur={(e) => {
                        if (
                            setInputFocus && (
                                !e.relatedTarget ||
                                e.relatedTarget &&
                                !e.relatedTarget.className.includes('kui-section-accordion-button')
                            )
                        ) {
                            setInputFocus(false);
                        }
                        if (
                            !isSmall ||
                            e.relatedTarget &&
                            (e.relatedTarget.className.includes(CLASS_CARD_DETAILS + '__color-tags-item') ||
                            e.relatedTarget.className.includes('kui-section-accordion-button'))
                        ) {
                            return;
                        }
                        setFocus(false);
                    }}
                />
                {isShowSave &&
                    <Button
                        className={className + '__button-new'}
                        onClick={onNewClick}
                        disabled={isButtonDisabled}
                        variant={'text'}
                    >
                        {colorLabel ?
                            isSmall ? COLOR_TAGS_BUTTON_EDIT_SHORT : COLOR_TAGS_BUTTON_EDIT
                            : isSmall ? COLOR_TAGS_BUTTON_NEW_SHORT : COLOR_TAGS_BUTTON_NEW
                        }
                    </Button>
                }
            </div>
            {!colorLabel && !isEdit &&
                <>
                    {!isSmall && <CardColorTagsTooltipHOC/>}
                    <div className={className + '__colors'}>
                        {isShowRecent &&
                            <ColorTagsSelect
                                title={''}
                                colors={showRecentAndCardColors}
                                checkedColors={colorTags}
                                disableAll={isLimit}
                                onChange={onColorSelect}
                                isHideTitle
                                isSmall={isSmall}
                                opened
                                onSelect={onColorEditSelect}
                                onClick={() => {}}
                                asidePanelWidth={asidePanelWidth}
                            />
                        }
                        {(!isSmall || !recentAndCardColors.length || isFocus || query) &&
                            <>
                                {(isSmall && !recentAndCardColors.length && isFocus && !query && !recentAndCardColors.length) || isAllColorsOpened || (query.length && !availableBoardCardColors.length) ? null
                                    : <ColorTagsSelect
                                        title={CARD_COLOR_TAGS_BOARD}
                                        colors={availableBoardCardColors}
                                        checkedColors={colorTags}
                                        disableAll={isLimit}
                                        onChange={onColorSelect}
                                        isHideTitle={!availableBoardColors.length || (isSmall && !recentAndCardColors.length)}
                                        isSmall={isSmall}
                                        opened={isBoardColorsOpened || !!query.length}
                                        onSelect={onColorEditSelect}
                                        onClick={onSectionUsedClick}
                                        onOpen={() => setBoardColorsOpened(true)}
                                        asidePanelWidth={asidePanelWidth}
                                    />
                                }
                                {(isSmall && !recentAndCardColors.length && !!availableBoardCardColors.length && !isFocus) || !availableBoardColors.length || (query.length && !availableBoardColors.length) ? null
                                    : <ColorTagsSelect
                                        title={CARD_COLOR_TAGS_ALL}
                                        colors={availableBoardColors}
                                        checkedColors={colorTags}
                                        disableAll={isLimit}
                                        onChange={onColorSelect}
                                        opened={isAllColorsOpened || !!query.length || isAllColorsForceOpened || isSmall && !!recentAndCardColors.length || !availableBoardCardColors.length}
                                        isHideTitle={isAllColorsOpened}
                                        isSmall={isSmall}
                                        onSelect={onColorEditSelect}
                                        onClick={onSectionAllClick}
                                        asidePanelWidth={asidePanelWidth}
                                    />
                                }
                                {(onOpenSettings && !isSmall || (onOpenSettings && isSmall && (recentAndCardColors.length || isFocus || query))) &&
                                    <Button
                                        className={className + '__button'}
                                        text={EDIT_COLOR_TAGS}
                                        variant={'icon-text'}
                                        onClick={onOpenSettings}
                                    >
                                        <Icon
                                            size={24}
                                            xlink={'pen'}
                                        />
                                    </Button>
                                }
                            </>
                        }
                    </div>
                </>
            }
            {isEdit &&
                <ColorPalette
                    selectedColor={color}
                    isNoRaws
                    onSelect={onColorChange}
                    colors={[...DEFAULT_COLORS, ...customColors]}
                    variant={paletteVariant}
                    isColorPicker
                />
            }
        </div>
    );
};
